import { useState } from 'react';
import { P } from '@stockpilot/shared';
import { useCreateUnitMutation, type Unit } from '@/api/endpoints';
import { apiFieldErrors } from '@/api/baseApi';
import { Input, Select } from '@/components/ui';
import { useAccess } from '@/hooks/useAccess';
import { CreatableSelect } from '@/components/CreatableSelect';

const DIMENSIONS = ['COUNT', 'WEIGHT', 'VOLUME', 'LENGTH', 'AREA', 'TIME'] as const;

const emptyUnit = { name: '', symbol: '', dimension: 'COUNT', precision: '2' };

interface UnitPickerProps {
  label: string;
  value: string;
  onChange: (unitId: string) => void;
  units: Unit[];
  /** Units already spoken for — a product's base unit, or one already added. */
  excludeIds?: string[];
  hint?: string;
  error?: string;
  required?: boolean;
}

/** A unit dropdown that can create the unit it is missing. */
export function UnitPicker({
  label,
  value,
  onChange,
  units,
  excludeIds = [],
  hint,
  error,
  required,
}: UnitPickerProps) {
  const { can } = useAccess();
  const [createUnit, createState] = useCreateUnitMutation();
  const [form, setForm] = useState(emptyUnit);

  const fieldErrors = apiFieldErrors(createState.error);
  const excluded = new Set(excludeIds);

  return (
    <CreatableSelect
      label={label}
      noun="unit"
      required={required}
      value={value}
      onChange={onChange}
      hint={hint}
      error={error}
      options={units
        .filter((unit) => !excluded.has(unit.id))
        .map((unit) => ({ id: unit.id, label: `${unit.name} (${unit.symbol})` }))}
      canCreate={can(P.UNIT_MANAGE)}
      creating={createState.isLoading}
      createDisabled={!form.name || !form.symbol}
      onReset={() => setForm(emptyUnit)}
      onCreate={async () => {
        const created = await createUnit({
          name: form.name,
          symbol: form.symbol,
          dimension: form.dimension as Unit['dimension'],
          precision: Number(form.precision),
        }).unwrap();
        return created.id;
      }}
      form={
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Name"
              placeholder="Carton"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              error={fieldErrors.name}
            />
            <Input
              label="Symbol"
              placeholder="ctn"
              value={form.symbol}
              onChange={(event) => setForm({ ...form, symbol: event.target.value })}
              error={fieldErrors.symbol}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Measures"
              value={form.dimension}
              onChange={(event) => setForm({ ...form, dimension: event.target.value })}
              hint="Conversions are only allowed within one of these."
            >
              {DIMENSIONS.map((dimension) => (
                <option key={dimension} value={dimension}>
                  {dimension.charAt(0) + dimension.slice(1).toLowerCase()}
                </option>
              ))}
            </Select>
            <Input
              label="Decimal places"
              type="number"
              min="0"
              max="6"
              value={form.precision}
              onChange={(event) => setForm({ ...form, precision: event.target.value })}
              error={fieldErrors.precision}
            />
          </div>
        </>
      }
    />
  );
}
